import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoSizer, Column, Table } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { useSelector } from 'react-redux';
import ModalWrapper from '../primitives/ModalWrapper';

const PriceAlertsVirtualList = ({
    subHeaderColor,
    systemColor,
    isBottomPanelPinned,
    linkColor,
    onDeleteAlert
}) => {
    const { t } = useTranslation();
    const { priceAlerts } = useSelector((state) => state.account.priceAlerts);
    const [alertToDelete, setAlertToDelete] = useState(null);
    
    const statusColorOptions = [
        { DisplayName: '#4CAF50', Value: "Active" },
        { DisplayName: '#F44336', Value: "Canceled" },
        { DisplayName: '#FFC107', Value: "Triggered" },
    ];
    
    // Convert object to array for rendering
    const priceAlertsToRender = Object.values(priceAlerts);
    
    return (
        <>
            <div className='full-height' style={systemColor}>
                <AutoSizer disableHeight={!isBottomPanelPinned}>
                    {({ height, width }) => (
                        <Table
                            width={width}
                            height={isBottomPanelPinned ? height : 210}
                            headerHeight={33}
                            headerStyle={subHeaderColor}
                            rowHeight={35}
                            rowCount={priceAlertsToRender.length}
                            rowGetter={({ index }) => priceAlertsToRender[index]}
                        >
                            <Column 
                                label={t('PriceAlerts.Symbol')} 
                                dataKey='symbol' 
                                width={126} 
                                className=''
                            />

                            <Column 
                                label={t('PriceAlerts.Side')} 
                                dataKey='side' 
                                width={100} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span>
                                        {cellData}
                                    </span>
                                )}
                            />
                                
                            <Column 
                                label={t('PriceAlerts.Condition')} 
                                dataKey='condition' 
                                width={100} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span>
                                        {cellData}
                                    </span>
                                )}
                            />

                            <Column 
                                label={t('PriceAlerts.Price')} 
                                dataKey='price' 
                                width={100} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span>
                                        {Number(cellData).toFixed(4)}
                                    </span>
                                )}
                            />

                            <Column 
                                label={t('PriceAlerts.Status')} 
                                dataKey='status' 
                                width={100} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span style={{ color: statusColorOptions.find(o => o.Value === cellData)?.DisplayName }}>
                                        {cellData}
                                    </span>
                                )}
                            />
                            <Column 
                                label={t('PriceAlerts.Created')} 
                                dataKey='created' 
                                width={150} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span>
                                        {new Date(cellData).toLocaleString()}
                                    </span>
                                )}
                            />
                            <Column 
                                label={t('PriceAlerts.Comment')} 
                                dataKey='comment' 
                                width={150} 
                                className=''
                                cellRenderer={({ cellData }) => (
                                    <span style={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        maxWidth: '140px'
                                    }}>
                                        {cellData || '-'}
                                    </span>
                                )}
                            />
                            <Column 
                                label={t('PriceAlerts.Actions')} 
                                dataKey='id' 
                                width={100} 
                                className=''
                                cellRenderer={({ cellData, rowData }) => {
                                    const isActive = rowData.status === "Active";
                                    return (
                                        <div>
                                            <button 
                                                style={{ 
                                                    background: 'none', 
                                                    border: 'none', 
                                                    color: linkColor?.color || '#007bff',
                                                    cursor: 'pointer',
                                                    marginRight: '10px'
                                                }}
                                                onClick={() => console.log('Edit alert', cellData)}
                                            >
                                                {t('Common.Edit')}
                                            </button>
                                            <button 
                                                style={{ 
                                                    background: 'none', 
                                                    border: 'none', 
                                                    color: isActive ? '#F44336' : '#999999',
                                                    cursor: isActive ? 'pointer' : 'not-allowed',
                                                    opacity: isActive ? 1 : 0.6
                                                }}
                                                onClick={() => isActive && setAlertToDelete(cellData)}
                                                disabled={!isActive}
                                            >
                                                {t('Common.Delete')}
                                            </button>
                                        </div>
                                    );
                                }}
                            />
                        </Table>
                    )}
                </AutoSizer>
            </div>
            {alertToDelete && (
                <ModalWrapper
                    open={!!alertToDelete}
                    heading={t('PriceAlerts.DeleteConfirmation')}
                    handleClose={() => setAlertToDelete(null)}
                    BackdropProps={{ className: 'outer-exclude' }}
                    childClassName='modal--small outer-exclude'
                >
                    <div style={{ padding: '20px' }}>
                        <p>{t('PriceAlerts.DeleteConfirmationMessage')}</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button 
                                className="btn"
                                style={{ 
                                    minWidth: '100px',
                                    marginRight: '10px',
                                    padding: '8px 16px',
                                    backgroundColor: 'var(--secondary-bg-color)',
                                    color: 'var(--main-text-color)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setAlertToDelete(null)}
                            >
                                {t('Common.Cancel')}
                            </button>
                            <button 
                                className="btn btn--general"
                                style={{ 
                                    minWidth: '100px',
                                    padding: '8px 16px',
                                    backgroundColor: 'var(--tertiary-bg-color)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    onDeleteAlert && onDeleteAlert(alertToDelete);
                                    setAlertToDelete(null);
                                }}
                            >
                                {t('Common.Delete')}
                            </button>
                        </div>
                    </div>
                </ModalWrapper>
            )}
        </>
    );
};

export default PriceAlertsVirtualList;
